import React, { useState, useEffect } from 'react';
import { apiCall } from '../../../../services/realApi';
import { Modal, Button } from '../../../../components/ui';
import {
  FiMail as Mail,
  FiHome as Building,
  FiShield as Shield,
  FiUsers as Users,
  FiClock as Clock,
  FiUserPlus as UserPlus,
  FiChevronRight as ChevronRight
} from 'react-icons/fi';

interface ModalUser {
  user_id: string;
  name?: string | null;
  email?: string | null;
  manager_id?: string | null;
}

interface UserInvitation {
  inviter_id: string;
  invited_user_id: string;
  invited_user_name?: string | null;
  inviter_name?: string | null;
  invited_at?: string | null;
  inherited_permissions?: boolean;
  inherited_clients?: number;
  inherited_modules?: number;
  inherited_roles?: number;
}

interface UserDetails extends ModalUser {
  job_title?: string | null;
  job_title_level?: number | null;
  client_count?: number;
  module_count?: number;
  role_count?: number;
  last_active?: string | null;
  is_active?: boolean;
}

interface UserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string | null;
  invitations?: UserInvitation[];
  allUsers?: ModalUser[];
  onInviteAsReport: (user: UserDetails) => void;
  currentUserId?: string;
  canInviteAWSUser?: boolean;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  isOpen,
  onClose,
  userId,
  invitations = [],
  allUsers = [],
  onInviteAsReport,
  currentUserId: _currentUserId,
  canInviteAWSUser = false
}) => {
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [directReports, setDirectReports] = useState<ModalUser[]>([]);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserDetails();
    } else {
      setUserDetails(null);
      setDirectReports([]);
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  const loadUserDetails = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch user details
      const userData = await apiCall(`/aws-users/${userId}`) as UserDetails;
      setUserDetails(userData);

      // Find direct reports from allUsers
      const reports = allUsers.filter(u => u.manager_id === userId);
      setDirectReports(reports);
    } catch (err: unknown) {
      console.error('Error loading user details:', err);
      setError((err instanceof Error ? err.message : null) || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const getJobTitleColor = (level: number) => {
    switch (level) {
      case 1:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300';
      case 2:
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 3:
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
      case 4:
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatJobTitle = (jobTitle: string | null | undefined) => {
    if (!jobTitle) return 'N/A';
    return jobTitle
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length > 1) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getUserInvitations = () => {
    if (!userId) return [];
    return invitations.filter(inv => 
      inv.inviter_id === userId || inv.invited_user_id === userId
    );
  };

  const getManager = () => {
    if (!userDetails?.manager_id) return null;
    return allUsers.find(u => u.user_id === userDetails.manager_id);
  };

  if (!isOpen) return null;

  const manager = getManager();
  const userInvitations = getUserInvitations();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="User Details"
      maxWidth="max-w-3xl"
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      ) : userDetails ? (
        <div className="space-y-6">
          {/* User Header */}
          <div className="flex items-start space-x-4 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="h-16 w-16 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-medium">
              {getInitials(userDetails.name)}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {userDetails.name}
              </h3>
              <div className="flex items-center space-x-2 mt-1">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {userDetails.email}
                </span>
              </div>
              {userDetails.job_title_level && (
                <div className="mt-2">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getJobTitleColor(userDetails.job_title_level)}`}>
                    Level {userDetails.job_title_level}: {formatJobTitle(userDetails.job_title)}
                  </span>
                </div>
              )}
            </div>
            {canInviteAWSUser && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  onInviteAsReport(userDetails);
                  onClose();
                }}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Report
              </Button>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-1">
                <Building className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Clients</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {userDetails.client_count || 0}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-1">
                <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Modules</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {userDetails.module_count || 0}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Roles</span>
              </div>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {userDetails.role_count || 0}
              </p>
            </div>
          </div>

          {/* Hierarchy Information */}
          <div className="grid grid-cols-2 gap-4">
            {/* Manager */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Manager
              </h4>
              {manager ? (
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                    {getInitials(manager.name)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {manager.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {manager.email}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No manager assigned</p>
              )}
            </div>

            {/* Direct Reports */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Direct Reports ({directReports.length})
              </h4>
              {directReports.length > 0 ? (
                <div className="space-y-2">
                  {directReports.slice(0, 3).map((report) => (
                    <div key={report.user_id} className="flex items-center space-x-2">
                      <div className="h-8 w-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-medium">
                        {getInitials(report.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {report.name}
                        </p>
                      </div>
                    </div>
                  ))}
                  {directReports.length > 3 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      +{directReports.length - 3} more
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No direct reports</p>
              )}
            </div>
          </div>

          {/* Invitation History */}
          {userInvitations.length > 0 && (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Invitation History
              </h4>
              <div className="space-y-3">
                {userInvitations.map((inv) => (
                  <div key={`${inv.inviter_id}-${inv.invited_user_id}`} className="flex items-start space-x-3 pb-3 border-b border-gray-200 dark:border-gray-700 last:border-0 last:pb-0">
                    <ChevronRight className="w-4 h-4 text-gray-400 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {inv.inviter_id === userId ? (
                          <>
                            <span className="font-medium">{inv.invited_user_name}</span> was invited by{' '}
                            <span className="font-medium">{inv.inviter_name}</span>
                          </>
                        ) : (
                          <>
                            <span className="font-medium">{inv.inviter_name}</span> invited{' '}
                            <span className="font-medium">{inv.invited_user_name}</span>
                          </>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {formatDate(inv.invited_at)}
                      </p>
                      {inv.inherited_permissions && (
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
                          <span>{inv.inherited_clients || 0} clients</span>
                          <span>{inv.inherited_modules || 0} modules</span>
                          <span>{inv.inherited_roles || 0} roles</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Last active: {formatDate(userDetails.last_active)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`h-2 w-2 rounded-full ${userDetails.is_active ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {userDetails.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
};

export default UserDetailsModal;

